import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { useState } from "react";
import { NameField } from "./form-fields/NameField";
import { CategoryField } from "./form-fields/CategoryField";
import { HeadlineField } from "./form-fields/HeadlineField";
import { BioField } from "./form-fields/BioField";
import { usePersonalInfoSubmit } from "./hooks/usePersonalInfoSubmit";
import { personalInfoSchema } from "./schema";
import { PhotoUpload } from "./components/photo-upload/PhotoUpload";
import { FormActions } from "./components/FormActions";
import { VerificationHeader } from "./components/VerificationHeader";

interface PersonalInfoFormProps {
  profile: any;
  onUpdate?: () => void;
  selectedRole?: string;
  onComplete?: () => void;
  onSkip?: () => void;
  showPhotoUpload?: boolean;
}

export function PersonalInfoForm({ 
  profile, 
  onUpdate, 
  selectedRole, 
  onComplete, 
  onSkip,
  showPhotoUpload = false
}: PersonalInfoFormProps) {
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url);
  
  const form = useForm<any>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      full_name: profile?.full_name || "",
      headline: profile?.headline || "",
      bio: profile?.bio || "",
      category_id: profile?.category_id || "",
    },
  });

  const { onSubmit, loadingCategories } = usePersonalInfoSubmit(profile, onUpdate);

  const handleFormSubmit = async (values: any) => {
    await onSubmit(values);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <VerificationHeader />

        {showPhotoUpload && (
          <div className="flex flex-col items-center gap-8">
            <PhotoUpload 
              profile={profile}
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
            />
          </div>
        )}

        <NameField control={form.control} />
        <HeadlineField control={form.control} />
        <CategoryField control={form.control} profile={profile} />
        <BioField control={form.control} />

        <FormActions 
          loadingCategories={loadingCategories}
          selectedRole={selectedRole}
          onSkip={onSkip}
        />
      </form>
    </Form>
  );
}
